import api from './api';
import { withCache, retryWithBackoff } from '../utils/apiUtils';

// Add a map to track in-progress phase executions
const inProgressPhaseExecutions = new Map();

export async function executePhase(
  executionId: string, 
  phaseId: string, 
  committeeMode: string = 'sequential'
) {
  // Generate a unique key for this execution
  const executionKey = `${executionId}-${phaseId}`;
  
  // Check if this phase execution is already in progress
  if (inProgressPhaseExecutions.has(executionKey)) {
    console.log(`Phase execution already in progress for ${phaseId}`);
    return inProgressPhaseExecutions.get(executionKey);
  }
  
  try {
    console.log(`Executing phase ${phaseId} with committee mode: ${committeeMode}`);
    console.log(`DETAILED DEBUG: Executing phase ${phaseId} with committee mode: ${committeeMode}`);
    console.log(`DETAILED DEBUG: Execution ID: ${executionId}`);
    
    // Create a promise for this execution
    const executionPromise = new Promise(async (resolve, reject) => {
      try {
        // Log more detailed information about the committee mode
        const modeDetails = {
          sequential: 'Personas will execute one after another, building on previous work',
          parallel: 'All personas will execute simultaneously',
          iterative: 'Multiple rounds of sequential execution will be performed',
          collaborative: 'Personas will work with a shared workspace',
          debate: 'Personas will propose, critique, and respond to each other'
        };
        
        console.log(`Committee mode details: ${modeDetails[committeeMode as keyof typeof modeDetails] || 'Custom mode'}`);
        
        // Check if the phase has a reviewer assigned
        const phaseInputs = await getPhaseInputs(executionId, phaseId);
        
        // Check phase status before executing
        if (phaseInputs.status === 'completed') {
          const error = new Error('Phase is already completed');
          error.response = { 
            status: 400, 
            data: { detail: 'Phase is in completed state and cannot be executed' } 
          };
          throw error;
        }
        
        const hasReviewer = phaseInputs?.config?.enable_review && 
                            (phaseInputs?.config?.reviewer_persona_id || 
                             phaseInputs?.config?.review_behavior === 'chair_role');
        
        if (hasReviewer) {
          console.log(`Phase has a reviewer assigned. Review behavior: ${phaseInputs?.config?.review_behavior}`);
          if (phaseInputs?.config?.reviewer_persona_id) {
            console.log(`Reviewer persona ID: ${phaseInputs.config.reviewer_persona_id}`);
          }
        }
        
        // Log the request details
        const requestData = { 
          phase_id: phaseId,
          committee_mode: committeeMode,
          enable_review: hasReviewer
        };
        console.log(`DETAILED DEBUG: Request data:`, requestData);
        
        // Call the generic phase execution endpoint with committee mode
        // Use a much longer timeout (10 minutes) without retries
        try {
          const response = await api.post(
            `/workflows/executions/${executionId}/run_phase`,
            requestData,
            { timeout: 600000 } // 10 minute timeout
          );
          
          resolve(response.data);
        } catch (error) {
          // If we still get a timeout after 10 minutes, it's probably a very complex task
          if (error.message && error.message.includes('timeout')) {
            console.log('Phase execution request timed out after 10 minutes. The operation is likely still running on the server.');
            
            // Return a partial success response
            resolve({
              message: "Phase execution started but is taking longer than expected. The operation is continuing in the background. Check back later for results.",
              execution_id: executionId,
              phase_id: phaseId,
              status: "in_progress",
              background_processing: true
            });
          } else {
            reject(error);
          }
        }
      } catch (error) {
        reject(error);
      } finally {
        // Remove from in-progress map when done
        inProgressPhaseExecutions.delete(executionKey);
      }
    });
    
    // Store the promise in the map
    inProgressPhaseExecutions.set(executionKey, executionPromise);
    
    // Return the promise
    return executionPromise;
  } catch (error) {
    // Handle errors and cleanup
    inProgressPhaseExecutions.delete(executionKey);
    
    console.error('Error executing phase:', error);
    console.error('DETAILED DEBUG: Error details:', error.response?.data || error.message);
    
    // Extract the detailed error message if available
    let errorMessage = 'Unknown error';
    let errorDetail = null;
    
    if (error.response?.data?.detail) {
      errorDetail = error.response.data.detail;
      errorMessage = `Server error: ${errorDetail}`;
    } else if (error.message) {
      errorMessage = error.message;
    }
    
    // Create a more user-friendly error message based on the error
    if (errorDetail === 'Phase is in completed state and cannot be executed') {
      errorMessage = 'This phase has already been completed and cannot be executed again.';
    } else if (error.message && error.message.includes('timeout')) {
      errorMessage = 'The server took too long to respond. The operation might still be processing in the background.';
    }
    
    // Dispatch a custom event to notify components about the phase execution error
    const customEvent = new CustomEvent('phase-execution-error', { 
      detail: { 
        executionId, 
        phaseId, 
        error: errorMessage,
        originalError: error
      } 
    });
    window.dispatchEvent(customEvent);
    
    // Enhance error object with user-friendly message
    const enhancedError = error;
    enhancedError.userMessage = errorMessage;
    throw enhancedError;
  }
}

export async function getPhaseInputs(executionId: string, phaseId: string) {
  try {
    console.log(`[PHASE-INPUTS-SERVICE] Getting inputs for phase ${phaseId} in execution ${executionId}`);
    
    // Use withCache and retryWithBackoff for better handling of rate limiting
    return withCache(
      `phase_inputs_${executionId}_${phaseId}`,
      async () => {
        console.log(`[PHASE-INPUTS-SERVICE] Cache miss, fetching from API`);
        
        // Log the exact URL being called
        const url = `/workflows/executions/${executionId}/phases/${phaseId}/inputs`;
        console.log(`[PHASE-INPUTS-SERVICE] Making API request to: ${url}`);
        
        const response = await retryWithBackoff(
          async () => {
            console.log(`[PHASE-INPUTS-SERVICE] Executing API call`);
            return api.get(url);
          },
          3,                // 3 retries
          2000,             // Start with 2s delay
          30000,            // Max 30s delay
          (error) => error.response?.status === 429 || error.code === 'ECONNABORTED'
        );
        
        // Log summary of inputs
        const inputs = response.data;
        console.log(`[PHASE-INPUTS-SERVICE] Phase inputs retrieved: ${inputs.personas?.length || 0} personas, ${inputs.tools?.length || 0} tools, ${inputs.artifacts?.length || 0} artifacts, ${inputs.prompts?.length || 0} prompts`);
        
        // Check if there's an error in the response
        if (inputs.error) {
          console.error(`[PHASE-INPUTS-SERVICE] Error in phase inputs response: ${inputs.error}`);
        } else {
          console.log(`[PHASE-INPUTS-SERVICE] Successfully retrieved phase inputs`);
        }
        
        // Log detailed information about personas
        if (inputs.personas && inputs.personas.length > 0) {
          console.log(`[PHASE-INPUTS-SERVICE] Personas in response: ${JSON.stringify(inputs.personas)}`);
        } else {
          console.warn(`[PHASE-INPUTS-SERVICE] No personas found in phase inputs response`);
        }
        
        return inputs;
      },
      30000 // Cache for 30 seconds
    );
  } catch (error) {
    console.error('[PHASE-INPUTS-SERVICE] Error getting phase inputs:', error);
    console.error('[PHASE-INPUTS-SERVICE] Error details:', error.response?.data || error.message);
    throw error;
  }
}

// New function to track committee execution progress
export async function trackCommitteeProgress(executionId: string, phaseId: string, contextId: string) {
  try {
    console.log(`Tracking committee execution progress for phase ${phaseId}`);
    
    const response = await api.get(
      `/workflows/executions/${executionId}/phases/${phaseId}/committee/progress`,
      {
        params: { context_id: contextId }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error tracking committee progress:', error);
    throw error;
  }
}

// Add a function to check phase status
async function checkPhaseStatus(executionId: string, phaseId: string): Promise<string> {
  try {
    const execution = await api.get(`/workflows/executions/${executionId}`);
    const phase = execution.data.phases.find(p => p.id === phaseId);
    return phase ? phase.status : 'unknown';
  } catch (error) {
    console.error('Error checking phase status:', error);
    throw error;
  }
}
